// node modules
import { observable, action, autorun } from 'mobx';
// services
import RoutingService from 'modules/Routing';
// stores
import { LStorage } from 'modules/Storage';
// constants
import { DEFAULT_THEME, ReleaseNotesIndicatorStates } from './consts';

type TModalConfig = {
  closePosition: boolean;
  changeTheme: boolean;
  chartSaveSettings: boolean;
  optionChain: boolean;
  orderReview: boolean;
  another: boolean;
  syncProgress: boolean;
  importantLinks: boolean;
  tbtVideoModal: boolean;
  priceAlerts: boolean;
  positionsMtmExitAllModal: boolean;
  releaseNotes: boolean;
  chartSyncDisclaimer: boolean;
  convertPosition: boolean;
};

type SetThemeCallback = (prevThemeValue: ThemeKeys) => ThemeKeys;

const DEFAULT_MODAL_CONFIG: TModalConfig = {
  closePosition: false,
  changeTheme: false,
  chartSaveSettings: false,
  optionChain: false,
  orderReview: false,
  another: false,
  syncProgress: false,
  importantLinks: false,
  tbtVideoModal: false,
  priceAlerts: false,
  positionsMtmExitAllModal: false,
  releaseNotes: false,
  chartSyncDisclaimer: false,
  convertPosition: false,
};
export default class AppStore {
  @observable utmSource = 'tv';
  @observable prevTheme: ThemeKeys = this.prevAppTheme;
  @observable theme: ThemeKeys = this.appTheme;
  @observable isOrderReviewEnabled: OrderReviewState = this
    .getIsOrderReviewEnabled;

  @observable modalsConfig: TModalConfig = { ...DEFAULT_MODAL_CONFIG };

  @observable isChartLayoutUpdated = false;
  @observable isOffline = false;
  @observable releaseNotesIndicatorState
    = ReleaseNotesIndicatorStates.RELEASE_NOTES;

  // autoruns
  changeThemeAutorun = autorun(() => {
    LStorage.sync('THEME', this.theme);
    LStorage.sync('PREV_THEME', this.prevTheme);
    LStorage.sync('ORDER_REVIEW_ENABLED', this.isOrderReviewEnabled);
  });

  // actions
  @action setTheme = (setter: ThemeKeys | SetThemeCallback,
    prevTheme: ThemeKeys) => {
    const newTheme = typeof setter === 'function' ? setter(this.theme) : setter;
    this.theme = newTheme;
    this.prevTheme = prevTheme;
  };

  @action setOrderReview = (status: OrderReviewState) => {
    this.isOrderReviewEnabled = status;
  };

  // actions
  @action getTheme = () => this.theme;
  @action getPrevTheme = () => this.prevTheme;
  @action setQueryParams = ({ utm_source }: AppQueryObject) => {
    if (utm_source) {
      this.utmSource = utm_source;
    }
  };

  @action toggleModal = (modalKey: keyof TModalConfig) => {
    this.modalsConfig[modalKey] = !this.modalsConfig[modalKey];
  };

  @action setModal = (modalKey: keyof TModalConfig, value: boolean) => {
    this.modalsConfig[modalKey] = value;
  };

  @action setIsChartLayoutUpdated = (layoutUpdated: boolean) => {
    this.isChartLayoutUpdated = layoutUpdated;
  }

  @action setIsOffline = (isOffline: boolean) => { this.isOffline = isOffline; }

  @action setReleaseNotesIndicatorState =
    (status: ReleaseNotesIndicatorStates) => {
      this.releaseNotesIndicatorState = status;
    }

  get appTheme(): ThemeKeys {
    const routeData = RoutingService.getQuery<'BASE'>().theme as ThemeKeys;
    const storageData = LStorage.getItem<ThemeKeys>('THEME');
    return routeData || storageData || DEFAULT_THEME;
  }

  get prevAppTheme(): ThemeKeys {
    const storageData = LStorage.getItem<ThemeKeys>('PREV_THEME');
    if (storageData) {
      return storageData;
    }

    let prevAppTheme = this.appTheme;
    if (prevAppTheme === 'auto') {
      const isDarkTheme = window?.matchMedia('(prefers-color-scheme: dark)')?.matches;
      prevAppTheme = isDarkTheme ? 'dark' : 'light';
    }
    return prevAppTheme;
  }

  get getIsOrderReviewEnabled(): OrderReviewState {
    const orderReview = LStorage.getItem<OrderReviewState>(
      'ORDER_REVIEW_ENABLED',
    );
    return orderReview || 'true';
  }
}
